/**
 * The item label, drawn dot for dot at the printer's resolution: the SKU, its
 * Code 128 barcode, then a short line on the left and the size on the right.
 * Ported from the ThriftyBusiness label; positions are for a 2" x 1" label and
 * scale with the medium's DPI. The same pixels go to the printer and to the
 * preview.
 */
import type { Media } from "@sable/label";

// A system font, so a label never waits on the web fonts to load.
const FONT = "Arial, Helvetica, sans-serif";

/* --- Code 128 ------------------------------------------------------------- */

// Bar/space module widths for symbol values 0–106 (106 is the stop).
const PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];
const START_B = 104, START_C = 105, CODE_C = 99, STOP = 106;
const QUIET_ZONE = 10; // modules of white either side, per the spec

/**
 * Symbol values for `text`: set B, switching to set C for a trailing run of
 * digits so a SKU's number packs two digits per symbol — fewer, wider bars.
 */
function code128Values(text: string): number[] {
  const digits = /\d*$/.exec(text)?.[0] ?? "";
  const paired = digits.length >= 4 ? digits.length - (digits.length % 2) : 0;
  const head = text.slice(0, text.length - paired);
  const tail = text.slice(text.length - paired);
  const values: number[] = [];
  if (head) {
    values.push(START_B);
    for (const ch of head) {
      const c = ch.charCodeAt(0);
      values.push(c >= 32 && c <= 127 ? c - 32 : 31); // anything unencodable prints as "?"
    }
    if (tail) values.push(CODE_C);
  } else {
    values.push(START_C);
  }
  for (let i = 0; i < tail.length; i += 2) values.push(Number(tail.slice(i, i + 2)));
  // The start symbol and the first data symbol both carry weight 1.
  const check = values.reduce((sum, v, i) => sum + v * Math.max(i, 1), 0) % 103;
  return [...values, check, STOP];
}

/** Alternating bar/space widths in modules, start symbol through stop. */
function code128Runs(text: string): number[] {
  return code128Values(text).flatMap((v) => [...PATTERNS[v]].map(Number));
}

/* --- Drawing -------------------------------------------------------------- */

/** The largest size from `maxPx` down to `minPx` at which `text` fits `width`. */
function fitPx(ctx: CanvasRenderingContext2D, text: string, width: number, maxPx: number, minPx: number): number {
  ctx.font = `${maxPx}px ${FONT}`;
  const w = ctx.measureText(text).width;
  return w <= width ? maxPx : Math.max(minPx, (maxPx * width) / w);
}

/** `text` cut to `width` with an ellipsis, in the context's current font. */
function clip(ctx: CanvasRenderingContext2D, text: string, width: number): string {
  if (ctx.measureText(text).width <= width) return text;
  let t = text;
  while (t && ctx.measureText(`${t}…`).width > width) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

/**
 * Draw the label. Positions are measured off a photo of a label already on
 * the stock (T-001581): code capitals 0.19"–0.40", bars 0.46"–0.76", the
 * bottom line's baseline at 0.87".
 */
export function drawLabel(canvas: HTMLCanvasElement, media: Media, dpi: number, sku: string, line: string, size: string) {
  const DOTS_W = Math.round(media.widthIn * dpi);
  const CANVAS_W = media.canvasWidthDots;
  const DOTS_H = media.heightDots;
  const pt = (points: number) => (points / 72) * dpi;
  const at = (inches: number) => Math.round(inches * dpi);
  canvas.width = CANVAS_W;
  canvas.height = DOTS_H;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, CANVAS_W, DOTS_H);
  ctx.fillStyle = "#000";
  ctx.textBaseline = "alphabetic";

  ctx.font = `${fitPx(ctx, sku, DOTS_W - at(0.1), pt(21), pt(12))}px ${FONT}`;
  ctx.textAlign = "center";
  ctx.fillText(sku, DOTS_W / 2, at(0.4));

  // Every module a whole number of dots, so no bar is rounded thicker than its
  // neighbours: as wide as fits with a quiet zone either side, capped at 3
  // dots (a 1.5" code for a T-###### SKU, close to the existing labels).
  const runs = code128Runs(sku);
  const modules = runs.reduce((a, b) => a + b, 0);
  const moduleDots = Math.max(1, Math.min(3, Math.floor(DOTS_W / (modules + 2 * QUIET_ZONE))));
  const barDots = modules * moduleDots;
  const left = Math.floor((DOTS_W - barDots) / 2);
  let x = left;
  runs.forEach((w, i) => {
    if (i % 2 === 0) ctx.fillRect(x, at(0.46), w * moduleDots, at(0.3)); // even positions are bars
    x += w * moduleDots;
  });

  // The description and size run edge to edge with the bars.
  ctx.font = `${pt(8)}px ${FONT}`;
  const sizeWidth = size ? ctx.measureText(size).width + at(0.06) : 0;
  if (size) {
    ctx.textAlign = "right";
    ctx.fillText(size, left + barDots, at(0.87));
  }
  const room = barDots - sizeWidth;
  ctx.font = `${fitPx(ctx, line, room, pt(8), pt(6))}px ${FONT}`;
  ctx.textAlign = "left";
  ctx.fillText(clip(ctx, line, room), left, at(0.87));
}
